import * as React from 'react';
import styled from '@emotion/styled';
import { useFormik } from 'formik';
import { Navigate, useNavigate } from 'react-router-dom';
import { BasicPageWidget } from '../../widgets/layout/BasicPageWidget';
import { InputContainerWidget } from '../../widgets/forms/InputContainerWidget';
import { TextInputType, TextInputWidget } from '../../widgets/forms/TextInputWidget';
import { ButtonType, ButtonWidget } from '../../widgets/controls/ButtonWidget';
import { useSystem } from '../../hooks/useSystem';
import { observer } from 'mobx-react';
import { FooterWidget } from '../../widgets/layout/FooterWidget';
import { Notification, NotificationType } from '../../core/stores/NotificationStore';
import { AuthCodes } from '@foxbun/lib-goferal-types';
import { RouteError } from '@foxbun/lib-errors';
import { Routing } from '../Routing';
import { Font } from '../../fonts';

const logo = require('../../../media/logo.png');

export const LoginPage: React.FC = observer(() => {
  const system = useSystem();
  const form = useFormik<{ email: string; password: string }>({
    initialValues: {
      email: null,
      password: null
    },
    isInitialValid: false,
    validate: async ({ email, password }) => {
      if (!email) {
        return {
          email: 'Required'
        };
      }
      if (!password) {
        return {
          password: 'Required'
        };
      }
      return false;
    },
    onSubmit: async ({ email, password }) => {
      try {
        //send to server
        await system.session_store.login({
          email: email,
          password: password
        });
      } catch (ex) {
        if (ex instanceof RouteError) {
          if (ex.error.code === AuthCodes.MAINTENANCE_MODE) {
            system.notification_store.showNotification(
              new Notification({
                type: NotificationType.ERROR,
                desc: 'We are currently in maintenance mode, please check our socials for more information!'
              })
            );
            return;
          }
        }
        system.notification_store.showNotification(
          new Notification({
            type: NotificationType.ERROR,
            desc: 'Log in failed, please try again'
          })
        );
        console.error('failed to log in', ex);
      }
    }
  });
  const navigate = useNavigate();
  if (system.user) {
    return <Navigate to={Routing.HOME} />;
  }

  return (
    <BasicPageWidget>
      <S.Container>
        <S.Logo src={logo} />
        <S.Phrase>
          The social network for
          <br />
          pups and handlers
        </S.Phrase>
        <InputContainerWidget label="Email">
          <TextInputWidget
            type={TextInputType.EMAIL}
            value={form.values.email}
            onChange={form.getFieldHelpers('email').setValue}
          />
        </InputContainerWidget>
        <InputContainerWidget label="Password">
          <TextInputWidget
            type={TextInputType.PASS}
            value={form.values.password}
            submit={() => {
              form.submitForm();
            }}
            onChange={form.getFieldHelpers('password').setValue}
          />
        </InputContainerWidget>
        <S.Buttons>
          <ButtonWidget
            disabled={!form.isValid}
            loading={form.isSubmitting}
            label="Log in"
            action={() => {
              form.submitForm();
            }}
          />
          <ButtonWidget
            type={ButtonType.SECONDARY}
            label="Forgot password"
            action={() => {
              navigate(Routing.FORGOT_PASSWORD);
            }}
          />
          <ButtonWidget
            type={ButtonType.SECONDARY}
            label="Register"
            action={() => {
              navigate(Routing.REGISTER);
            }}
          />
        </S.Buttons>
      </S.Container>
      <FooterWidget />
    </BasicPageWidget>
  );
});
namespace S {
  export const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 10px;
    justify-content: center;
    height: 100%;
  `;

  export const Buttons = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: 10px;
    align-items: stretch;
    width: 250px;
  `;

  export const Phrase = styled.div`
    color: ${(p) => p.theme.text.secondary};
    font-family: ${Font.Special};
    text-align: center;
    margin-bottom: 30px;
  `;

  export const Logo = styled.img`
    width: 300px;
  `;
}
